"use client";

import Link from "next/link";
import type { ReactNode } from "react";
import { useState } from "react";
import { cn } from "@/lib/utils";

export type InteractiveFAQItem = {
  id: string;
  title: string;
  answer: ReactNode;
  href?: string;
  hrefLabel?: string;
};

export type InteractiveFAQQuickLink = {
  href: string;
  highlight: string;
  text: string;
};

type InteractiveFAQProps = {
  title: string;
  subtitle: string;
  items: InteractiveFAQItem[];
  quickLinks?: InteractiveFAQQuickLink[];
  dense?: boolean;
  showAnswerFooter?: boolean;
  answerFooter?: ReactNode;
};

export function InteractiveFAQ({
  title,
  subtitle,
  items,
  quickLinks = [],
  dense = false,
  showAnswerFooter = false,
  answerFooter,
}: InteractiveFAQProps) {
  const [activeId, setActiveId] = useState(items[0]?.id ?? "");

  if (items.length === 0) return null;

  return (
    <section
      aria-label="Frequently asked questions"
      className="section-padding relative overflow-hidden"
    >
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, var(--colors-background) 0%, var(--colors-theme-blue-soft) 35%, var(--colors-background) 100%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(90deg, color-mix(in srgb, var(--colors-foreground) 10%, transparent) 0 1px, transparent 1px 28px)",
          }}
        />
        <div
          className="absolute -left-16 top-10 h-40 w-40 rounded-full"
          style={{
            background:
              "radial-gradient(circle, color-mix(in srgb, var(--colors-success) 18%, transparent), transparent 70%)",
          }}
        />
        <div
          className="absolute right-0 top-1/2 h-48 w-72 -translate-y-1/2"
          style={{
            background:
              "radial-gradient(ellipse, color-mix(in srgb, var(--colors-theme-blue) 16%, transparent), transparent 75%)",
          }}
        />
      </div>

      <div className="container relative mx-auto grid grid-cols-1 gap-8 md:grid-cols-12 md:gap-10">
        <div className={cn(dense ? "md:col-span-5" : "md:col-span-4")}>
          <h2 className="text-section-h2">{title}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>

          <div
            className={cn(
              "mt-5 flex flex-row gap-2 overflow-x-auto pb-1 md:overflow-visible",
              dense
                ? "md:grid md:max-h-[440px] md:grid-cols-2 md:gap-2 md:overflow-auto md:pr-1"
                : "md:flex-col",
            )}
          >
            {items.map((item, index) => {
              const isActive = item.id === activeId;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveId(item.id)}
                  className={cn(
                    "min-w-fit rounded-lg border px-3 py-2 text-left transition-all md:w-full",
                    dense ? "md:px-3 md:py-2.5" : "md:px-4 md:py-3",
                    isActive
                      ? "border-foreground bg-card shadow-sm"
                      : "border-border bg-card/60 hover:bg-muted",
                  )}
                >
                  <div className="flex items-start gap-2">
                    <span className="mt-0.5 font-data text-xs text-muted-foreground">
                      0{index + 1}
                    </span>
                    <p
                      className={cn(
                        "font-medium text-foreground",
                        dense ? "text-xs md:text-sm" : "text-sm md:text-[15px]",
                      )}
                    >
                      {item.title}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className={cn(dense ? "md:col-span-7" : "md:col-span-8")}>
          {items.map((item) => {
            const isActive = item.id === activeId;
            return (
              <div
                key={item.id}
                className={cn(
                  "flex flex-col justify-between rounded-2xl border border-border bg-card p-6 shadow-[0_10px_28px_rgba(0,0,0,0.05)] md:p-8",
                  !showAnswerFooter &&
                    (dense ? "min-h-[380px]" : "min-h-[320px]"),
                  !isActive && "hidden",
                )}
                style={
                  isActive
                    ? { animation: "hero-fade-up 0.35s ease both" }
                    : undefined
                }
              >
                <div>
                  <h3 className="text-section-h3">{item.title}</h3>
                  <div className="mt-4 text-sm leading-relaxed text-muted-foreground">
                    {item.answer}
                  </div>
                  {item.href && item.hrefLabel ? (
                    <Link
                      href={item.href}
                      className="mt-5 inline-flex text-sm font-medium text-foreground underline underline-offset-4"
                    >
                      {item.hrefLabel}
                    </Link>
                  ) : null}
                </div>

                {showAnswerFooter && answerFooter ? (
                  <div className="mt-6 border-t border-border pt-4">
                    {answerFooter}
                  </div>
                ) : null}

                {quickLinks.length > 0 ? (
                  <div className="mt-6 grid grid-cols-1 gap-3 border-t border-border pt-4 md:grid-cols-2">
                    {quickLinks.map((quickLink) => (
                      <Link
                        key={quickLink.href}
                        href={quickLink.href}
                        className="rounded-lg border border-border bg-secondary px-4 py-3 text-sm text-foreground transition-colors hover:bg-card"
                      >
                        <span className="font-medium text-foreground">
                          {quickLink.highlight}
                        </span>{" "}
                        {quickLink.text}
                      </Link>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

InteractiveFAQ.displayName = "InteractiveFAQ";
